import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Download, ShoppingBag, ArrowLeft, Loader2 } from "lucide-react";
import axios from "axios";
import { jsPDF } from "jspdf";

export default function ThankYou() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    if (!sessionId) {
      setLoading(false);
      return;
    }

    axios.post(`${API}/orders/verify`, { session_id: sessionId })
      .then((res) => {
        if (res.data.status === "success" || res.data.status === "already_paid") {
          setOrder(res.data.order || res.data);
        } else {
          setError("El pago aún no se ha confirmado o no ha sido completado.");
        }
      })
      .catch((err) => {
        console.error(err);
        setError("Ocurrió un error al verificar el pago con Stripe.");
      })
      .finally(() => setLoading(false));
  }, [sessionId, API]);

  const downloadPDF = () => {
    if (!order) return;

    const doc = new jsPDF();
    const customer = order.customer || {};
    const items = order.items || [];
    const orderId = (order.order_id || order.id || "0000").slice(0, 8).toUpperCase();
    const dateStr = order.created_at ? new Date(order.created_at).toLocaleDateString("es-ES") : new Date().toLocaleDateString("es-ES");

    // Header
    doc.setFillColor(17, 17, 17);
    doc.rect(0, 0, 210, 40, "F");
    
    doc.setTextColor(201, 169, 106); // Gold
    doc.setFont("serif", "bold");
    doc.setFontSize(22);
    doc.text("MAISON ÉLIXIR", 15, 22);
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(180, 180, 180);
    doc.text("FACTURA OFICIAL DE COMPRA", 15, 30);

    doc.setTextColor(255, 255, 255);
    doc.text(`Nº FACTURA: #FE-${orderId}`, 145, 22);
    doc.text(`FECHA: ${dateStr}`, 145, 30);

    // Customer & Order Info
    doc.setTextColor(40, 40, 40);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("DATOS DEL CLIENTE", 15, 52);
    doc.text("MÉTODO DE PAGO", 130, 52);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text(`Nombre: ${customer.name || ""} ${customer.surname || ""}`, 15, 60);
    doc.text(`Email: ${customer.email || ""}`, 15, 67);
    doc.text(`Teléfono: ${customer.phone || ""}`, 15, 74);
    doc.text(`Dirección: ${customer.address || ""}, ${customer.city || ""} (${customer.postal_code || ""})`, 15, 81);

    doc.text("Tarjeta de Crédito / Débito (Stripe)", 130, 60);
    doc.text("Estado: PAGADO Y CONFIRMADO", 130, 67);

    // Table Header
    let y = 98;
    doc.setFillColor(245, 243, 238);
    doc.rect(15, y, 180, 9, "F");
    
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(40, 40, 40);
    doc.text("PRODUCTO", 20, y + 6);
    doc.text("CANT.", 120, y + 6);
    doc.text("PRECIO UNIT.", 145, y + 6);
    doc.text("TOTAL", 175, y + 6);

    y += 15;
    doc.setFont("helvetica", "normal");

    items.forEach((item) => {
      doc.text(String(item.name), 20, y);
      doc.text(String(item.quantity), 125, y);
      doc.text(`${Number(item.price).toFixed(2)} €`, 145, y);
      doc.text(`${(Number(item.price) * Number(item.quantity)).toFixed(2)} €`, 175, y);
      y += 8;
    });

    // Shipping Cost
    doc.text("Gastos de Envío a Domicilio", 20, y);
    doc.text("1", 125, y);
    doc.text("3.00 €", 145, y);
    doc.text("3.00 €", 175, y);
    y += 12;

    doc.setLineWidth(0.5);
    doc.setDrawColor(200, 200, 200);
    doc.line(15, y, 195, y);
    y += 10;

    // Total
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.setTextColor(201, 169, 106);
    doc.text(`TOTAL PAGADO: ${Number(order.total || 0).toFixed(2)} €`, 125, y);

    // Footer
    doc.setFontSize(8);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(150, 150, 150);
    doc.text("Gracias por elegir Maison Élixir. Parfums d'Exception.", 105, 280, { align: "center" });

    doc.save(`Factura_MaisonElixir_#${orderId}.pdf`);
  };

  return (
    <div className="min-h-screen bg-noir flex items-center justify-center px-6 py-20 grain-overlay text-white">
      <div className="max-w-2xl w-full">
        {loading ? (
          <div className="text-center py-20">
            <Loader2 className="animate-spin text-gold mx-auto mb-4" size={40} />
            <p className="text-neutral-400 font-light text-sm uppercase tracking-[0.2em]">Verificando tu pago con Stripe...</p>
          </div>
        ) : error ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="border border-red-500/30 bg-red-500/10 p-8 text-center">
            <h2 className="text-xl font-serif-lux text-red-400 mb-2">Aviso de Confirmación</h2>
            <p className="text-neutral-300 text-sm font-light mb-6">{error}</p>
            <Link to="/" className="inline-flex items-center gap-2 border border-white/20 text-white px-6 py-3 text-xs uppercase tracking-widest hover:border-gold hover:text-gold transition-colors">
              <ArrowLeft size={16} /> Volver a la Tienda
            </Link>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="border border-white/10 bg-noir-soft p-8 sm:p-12 shadow-2xl relative">
            <div className="text-center mb-10 border-b border-white/10 pb-8">
              <CheckCircle2 size={56} className="text-gold mx-auto mb-4 stroke-[1.2]" />
              <span className="font-script-lux text-gold text-2xl">✦ Merci beaucoup ✦</span>
              <h1 className="font-serif-lux text-3xl sm:text-4xl tracking-[0.15em] uppercase text-white mt-2">¡Gracias por tu compra!</h1>
              <p className="text-neutral-400 text-sm font-light mt-3">Hemos recibido tu pago correctamente y te hemos enviado la confirmación por correo.</p>
            </div>

            {order && (
              <div className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4 bg-white/[0.02] border border-white/5 p-4 text-xs text-neutral-300">
                  <div>
                    <span className="text-neutral-500 uppercase tracking-widest block text-[9px] mb-1">Nº de Pedido</span>
                    <span className="font-mono text-gold font-bold">#{((order.order_id || order.id || "0000")).slice(0, 8).toUpperCase()}</span>
                  </div>
                  <div>
                    <span className="text-neutral-500 uppercase tracking-widest block text-[9px] mb-1">Estado de Pago</span>
                    <span className="text-emerald-400 font-medium uppercase tracking-wider">Pagado con Tarjeta (Stripe)</span>
                  </div>
                </div>

                {order.customer && (
                  <div className="text-xs text-neutral-300 space-y-1 bg-white/[0.02] border border-white/5 p-4">
                    <span className="text-neutral-500 uppercase tracking-widest block text-[9px] mb-2">Datos de Envío</span>
                    <p className="font-medium text-white">{order.customer.name} {order.customer.surname}</p>
                    <p className="text-neutral-400">{order.customer.address}, {order.customer.city} ({order.customer.postal_code})</p>
                    <p className="text-neutral-400">Tel: {order.customer.phone} | Email: {order.customer.email}</p>
                  </div>
                )}

                {order.items && order.items.length > 0 && (
                  <div className="border border-white/5 bg-white/[0.02] p-4 space-y-3">
                    <span className="text-neutral-500 uppercase tracking-widest block text-[9px]">Productos</span>
                    {order.items.map((it, idx) => (
                      <div key={idx} className="flex justify-between text-xs">
                        <span className="text-neutral-200">{it.name} <span className="text-neutral-500">x{it.quantity}</span></span>
                        <span className="text-gold">{(it.price * it.quantity).toFixed(2)} €</span>
                      </div>
                    ))}
                    <div className="flex justify-between text-xs border-t border-white/5 pt-2 text-neutral-400">
                      <span>Envío a domicilio</span>
                      <span>3.00 €</span>
                    </div>
                    <div className="flex justify-between text-sm font-serif-lux text-gold border-t border-white/10 pt-3">
                      <span>TOTAL PAGADO</span>
                      <span className="text-xl font-bold">{Number(order.total || 0).toFixed(2)} €</span>
                    </div>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-4 pt-6">
                  <button
                    onClick={downloadPDF}
                    className="flex-1 bg-gold text-black text-xs uppercase tracking-[0.2em] font-medium py-4 px-6 flex items-center justify-center gap-2 hover:bg-gold-light transition-colors"
                  >
                    <Download size={16} /> Descargar Factura PDF
                  </button>
                  <Link
                    to="/"
                    className="border border-white/20 text-white text-xs uppercase tracking-[0.2em] py-4 px-6 flex items-center justify-center gap-2 hover:border-gold hover:text-gold transition-colors"
                  >
                    <ShoppingBag size={16} /> Seguir Comprando
                  </Link>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
